import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import {
	useAttachmentQuery,
	useConvertSocialVideoMutation,
	useGetSystemJobQuery,
	useUploadVideoAttachmentMutation,
} from '../../../../queries';

export const useVideoUploadMutation = ({
	onError,
	onSuccess,
	impersonationContext,
	skipConversion = false,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (jobId: string) => void;
	impersonationContext?: Api.IImpersonationContext;
	skipConversion?: boolean;
}) => {
	const { mutateAsync: getPresignedUrlAsync } = useUploadVideoAttachmentMutation();
	const { mutateAsync: convertVideo } = useConvertSocialVideoMutation();

	return useMutation({
		mutationFn: async ({
			file,
			duration,
			height,
			width,
			postId,
			forUserId,
		}: {
			file: File;
			duration: number;
			height: number;
			width: number;
			postId: string;
			forUserId: string;
		}) => {
			const request: Api.IPresignedUrlRequest = {
				contentLength: file.size,
				contentType: file.type,
				duration,
				fileName: file.name,
				height,
				width,
			};

			const presignedResult = await getPresignedUrlAsync({ forUserId, request, impersonationContext });

			await fetch(presignedResult.presignedUrl, {
				body: file,
				headers: { 'Content-Type': file.type },
				method: 'PUT',
			});

			if (!skipConversion) {
				await convertVideo({ forUserId, jobId: presignedResult.jobId, postId, impersonationContext });
			}

			return presignedResult.jobId;
		},
		onError,
		onSuccess,
	});
};

export function useVideoAttachmentFromSystemJob({
	jobId,
	impersonationContext,
}: {
	jobId: string;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const [attachmentId, setAttachmentId] = useState<string>();
	const [errorMessage, setErrorMessage] = useState<string>();

	const isAttachmentQueryEnabled = Boolean(attachmentId);
	const { data: videoAttachment } = useAttachmentQuery({
		enabled: isAttachmentQueryEnabled,
		id: attachmentId,
		impersonationContext,
	});

	const isSystemJobQueryEnabled = Boolean(jobId);
	const systemJobQuery = useGetSystemJobQuery<Api.IMediaConvertSystemJob>({
		enabled: isSystemJobQueryEnabled,
		jobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (!jobId) {
				return false;
			}
			return !data?.attachmentId ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});
	React.useEffect(() => {
		if (systemJobQuery.data) {
			if (systemJobQuery.data.attachmentId) {
				setAttachmentId(systemJobQuery.data.attachmentId);
			}
			if (systemJobQuery.data.status?.length > 0) {
				setErrorMessage(systemJobQuery.data.status);
				return;
			}
			if (systemJobQuery.data.recordsFailed > 0) {
				setErrorMessage('Video conversion failed.');
			}
		}
	}, [systemJobQuery.data]);

	const resetErrorMessage = useCallback(() => {
		setErrorMessage(null);
	}, []);

	return { errorMessage, resetErrorMessage, videoAttachment };
}
